import { useEffect, useRef, useState } from "react";
import { Button, InputField, Tooltip } from "elements";
import { Accordion } from "react-bootstrap";

import { ReactComponent as CollapseIcon } from "assets/images/journeys/chevron-right.svg";
import { ReactComponent as TrashIcon } from "assets/images/journeys/trash-dark-fill.svg";
import { ReactComponent as WarningIcon } from "assets/images/journeys/warning-red.svg";

import {
  Wrapper,
  Head,
  Toggle,
  Title,
  Delete,
  Body,
  BodyInner,
  UnitWrapper
} from "./styled";

import {
  Group,
  Label,
  ActionsGroup
} from '../styled';

const MetricGroup = ({ activeKey, data, onToggle, onUnitAdd, onUnitUpdate, onUnitDelete, onMetricUpdate, onMetricDelete, allMetrics, allUnits }) => {
  const { id, label, units } = data;

  const [isUnitEdit, setIsUnitEdit] = useState(null);
  const [unitName, setUnitName] = useState('');
  const [labelName, setLabelName] = useState(label);

  const $label = useRef(null);
  const $inputRefs = useRef([]);

  const isActive = activeKey === id;

  useEffect(() => {
    setTimeout(() => {
      // Find the first input field that is empty
      const emptyField = $inputRefs.current.find(input => input && !input.value);
  
      if (emptyField) {
        emptyField.focus();
      } else if ($label && $label.current) {
        $label.current.focus();
      }
    }, 250);
  }, [activeKey, units.length]);

  useEffect(() => {
    $inputRefs.current = $inputRefs.current.slice(0, units.length);
  }, [units]);

  const onClickHandle = () => {
    onToggle(id);
  }

  const onWarningIconClick = (e) => {
    e.stopPropagation();
  }

  const onTooltipClick = (e) => {
    e.stopPropagation();
  }

  const onLabelChangeHandle = e => {
    let input = e.target.value;

    if (input.length > 12) {
      input = input.slice(0, 12);
    }

    setLabelName(input);
    onMetricUpdate(input, id);
  }

  const onLabelBlurHandle = e => {
    if (e.target.value === label)
      return;
  }

  const onMetricDeleteHandle = () => {
    onMetricDelete(id);
  }

  const onDeleteHandle = (unit) => () => {
    onUnitDelete(unit.id);
  }

  const onAddUnitHandle = () => {
    const emptyField = $inputRefs.current.find(input => input && !input.value);
    
    if (emptyField) {
      emptyField.focus();
    } else {
      onUnitAdd("", id);
    }
  }

  const onUnitChangeHandle = unit => e => {
    let input = e.target.value;

    if (input.length > 17) {
      input = input.slice(0, 17);
    }

    setUnitName(input);
    onUnitUpdate(input, unit.id);
  }

  const onUnitFocusHandle = unit => (e) => {
    setUnitName(unit.name);
    setIsUnitEdit(unit.id);
    
    // Scroll the focused input into view
    if (e.target) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
    }
  }

  const onUnitBlurHandle = () => () => {
    setUnitName('')
    setIsUnitEdit(null);
  }

  const isOnlyUnit = units.filter(unit => !unit.isDeleted).length === 1;
  
  // Check if label is empty or any non-deleted unit has an empty name
  const hasEmptyFields = !label || !label.trim() || units.some(unit => !unit.isDeleted && (!unit.name || !unit.name.trim()));

  // Check for duplicate metric labels
  const hasDuplicateMetric = allMetrics && allMetrics.filter(metric =>
    metric.label && metric.label.trim() &&
    metric.label.trim().toLowerCase() === label.trim().toLowerCase()
  ).length > 1;

  // Check for duplicate units within this metric
  const hasDuplicateUnits = () => {
    const unitNames = units
      .filter(unit => !unit.isDeleted)
      .map(unit => unit.name)
      .filter(name => name && name.trim())
      .map(name => name.trim().toLowerCase());
    
    return unitNames.length !== new Set(unitNames).size;
  };

  const hasDuplicates = hasDuplicateMetric || hasDuplicateUnits();

  // Determine tooltip message
  const getTooltipMessage = () => {
    if (hasEmptyFields) {
      return "This metric has empty fields. Please fill in all required fields.";
    }
    if (hasDuplicateMetric) {
      return "This metric name already exists. Please use a different name.";
    }
    if (hasDuplicateUnits()) {
      return "This metric has duplicate units. Please use unique unit names.";
    }
    return "";
  };

  const shouldShowWarning = hasEmptyFields || hasDuplicates;

  return (
    <Wrapper $active={isActive}>
      <Head>
        <Toggle $active={isActive} type="button" onClick={onClickHandle}>
          <CollapseIcon className="collapse-icon" />
          <Title $active={isActive}>{label}</Title>
          {shouldShowWarning && (
            <span onClick={onTooltipClick} style={{ display: 'inline-flex' }}>
              <Tooltip
                placement="rightTop"
                title={getTooltipMessage()}
              >
                <WarningIcon
                  style={{ marginLeft: -8, width: 16, height: 16, color: '#F59E0B', cursor: 'default' }}
                  onClick={onWarningIconClick}
                />
              </Tooltip>
            </span>
          )}
        </Toggle>

        {isActive && (<Delete onClick={onMetricDeleteHandle} type="button" title="Delete Metric">
          <TrashIcon />
        </Delete>)}
      </Head>

      <Accordion.Collapse as={Body} eventKey={id}>
        <BodyInner>
          <Group>
            <Label>Label*</Label>
            <InputField
              size="md"
              value={labelName}
              placeholder="Label Name"
              ref={$label}
              required
              onChange={onLabelChangeHandle}
              onBlur={onLabelBlurHandle}
              onKeyDown={e => e.key === 'Enter' && onLabelBlurHandle(e)}
            />
          </Group>

          {(units.filter(unit => !unit.isDeleted).length) ? <Group>
            <Label>Unit*</Label>

            {units.filter(unit => !unit.isDeleted).map((unit, i) => (
              <UnitWrapper key={unit.id}>
                <InputField
                  size="md"
                  value={isUnitEdit === unit.id ? unitName : unit.name}
                  placeholder="Unit Name"
                  required
                  ref={el => ($inputRefs.current[i] = el)}
                  onChange={onUnitChangeHandle(unit)}
                  onFocus={onUnitFocusHandle(unit)}
                  onBlur={onUnitBlurHandle(unit)}
                  onKeyDown={e => e.key === 'Enter' && onUnitBlurHandle(unit)}
                />

                <Delete
                  type="button"
                  className={unit === 0 ? 'ghost' : ''}
                  title="Delete Unit"
                  disabled={isOnlyUnit}
                  $isHoverable
                  onClick={onDeleteHandle(unit)}
                >
                  <TrashIcon />
                </Delete>
              </UnitWrapper>
            ))}
          </Group> : null}

          <ActionsGroup>
            <Button
              type="button"
              styleType="link-primary"
              onClick={onAddUnitHandle}
            >
              Add Unit
            </Button>
          </ActionsGroup>
        </BodyInner>
      </Accordion.Collapse>
    </Wrapper>
  );
}

export default MetricGroup;
